import { useCallback, useContext, useEffect, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useCheckPermission } from 'app/hooks/useCheckPermission'
import { UserPermissionsContext } from 'app/hooks/useUserPermission'
import { useInitialTableSort } from 'app/hooks/useInitialTableSort'
import { RoleJson } from 'app/types/entities'
import { showModal } from 'app/lib/modal'
import type { SearchResponse } from 'app/services/search'
import { useUser } from 'app/utils'
import { ORG_ROLES, WORKSPACE_ROLES } from 'lib/defaultRoles'
import { isSMCOrg } from 'lib/isSMCOrg'
import { RoleForm } from 'app/components/roles/RoleForm'
import { useRoleStore } from 'app/stores/StoreContext'

export type UseRolesReturn = {
  sortedRoles: SearchResponse<RoleJson> | undefined
  initialSort: ReturnType<typeof useInitialTableSort>
  canManageRoles: boolean
  defaultRoleNames: string[]
  isSMC: boolean
  openCreateModal: () => void
  openEditModal: (role: RoleJson) => void
}

export function useRoles(): UseRolesReturn {
  const [searchParams] = useSearchParams()
  const sort = searchParams.get(`sort`) === `` ? undefined : (searchParams.get(`sort`) ?? `name.keyword`)
  const roleStore = useRoleStore()
  const checkPermission = useCheckPermission()
  const canManageRoles = checkPermission([`org:settings:manage-roles`, `roles:manage`])
  const initialSort = useInitialTableSort(sort)
  const user = useUser()
  const isSMC = isSMCOrg(user?.orgId)
  const { permissions } = useContext(UserPermissionsContext)
  const grantablePermissions = useMemo(
    () => (user?.type === `super` ? undefined : (permissions ?? [])),
    [user?.type, permissions],
  )
  const defaultRoleNames = useMemo(
    () => [...WORKSPACE_ROLES.map((role) => role.name), ...ORG_ROLES.map((role) => role.name)],
    [],
  )

  const fetchRoles = useCallback(() => {
    const [field, direction] = (sort ?? `name.keyword:asc`).split(`:`)
    roleStore.fetchList({
      pagination: { page: 0, limit: 1000 },
      sort: { field, direction: (direction?.toUpperCase() as 'ASC' | 'DESC') ?? 'ASC' },
    })
  }, [sort, user?.workspaceId])

  useEffect(() => {
    fetchRoles()
  }, [fetchRoles])

  const roles = roleStore.list as SearchResponse<RoleJson> | undefined

  const sortedRoles = useMemo(() => {
    if (!roles?.hits) return roles

    const defaultRolesOrder = isSMC ? [`Admin`, `User`, `Limited User`] : [`Owner`, `Admin`, `User`, `Limited User`]
    const filteredHits = isSMC ? roles.hits.filter((r) => r.name !== `Owner`) : roles.hits

    const sorted = [...filteredHits].sort((a, b) => {
      const aIdx = defaultRolesOrder.indexOf(a.name!)
      const bIdx = defaultRolesOrder.indexOf(b.name!)
      if (aIdx !== -1 && bIdx !== -1) return aIdx - bIdx
      if (aIdx !== -1) return -1
      if (bIdx !== -1) return 1
      return (b.permissions || []).length - (a.permissions || []).length
    })

    return { ...roles, hits: sorted }
  }, [roles, isSMC])

  const handleRoleSuccess = useCallback(() => {
    const [field, direction] = (sort ?? `name.keyword:asc`).split(`:`)
    roleStore.fetchList(
      {
        pagination: { page: 0, limit: 1000 },
        sort: { field, direction: (direction?.toUpperCase() as 'ASC' | 'DESC') ?? 'ASC' },
      },
      { requestPolicy: 'network-only' },
    )
  }, [sort, roleStore])

  const openModal = useCallback(
    ({ title, role }: { title: string; role?: RoleJson }) => {
      showModal(
        <RoleForm
          role={role}
          onSuccess={handleRoleSuccess}
          orgId={user?.orgId}
          workspaceId={user?.workspaceId}
          userPermissions={grantablePermissions}
          isOrgRole={role ? !role.workspaceId : !user?.workspaceId}
        />,
        { title, isFullScreen: false, removePadding: true, useRadix: true },
      )
    },
    [handleRoleSuccess, user?.orgId, user?.workspaceId, grantablePermissions],
  )

  const openCreateModal = useCallback(() => openModal({ title: `Add New Role` }), [openModal])

  const openEditModal = useCallback(
    (role: RoleJson) => openModal({ title: `${role.isDefault ? `` : `Edit`} ${role.name}`, role }),
    [openModal],
  )

  return {
    sortedRoles,
    initialSort,
    canManageRoles,
    defaultRoleNames,
    isSMC,
    openCreateModal,
    openEditModal,
  }
}
